import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import '../styles/design-system.css';
import '../styles/pages.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Plus:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14" /></svg>),
  Folder: (p) => (<svg width="18" height="18" {...IP} {...p}><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" /></svg>),
  FolderBig: (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" /></svg>),
  Pencil: (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>),
  Trash:  (p) => (<svg width="12" height="12" {...IP} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>),
  Link:   (p) => (<svg width="10" height="10" {...IP} {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>),
};

const COLORS = [
  '#ff5436',
  '#8b9b3f',
  '#e8b94a',
  '#0d0d0f',
  '#c8d6a8',
  '#a8a8af',
  '#d63d20',
  '#6b6b72',
];

export default function FoldersPage() {
  const { workspace } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: COLORS[0] });

  useEffect(() => {
    if (!workspace?.workspace_id) return;
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.workspace_id]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('workspace_id', workspace.workspace_id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setFolders(data || []);
    } catch {
      toast.error('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      if (editing) {
        const { error } = await supabase
          .from('folders')
          .update({ name: form.name, color: form.color })
          .eq('folder_id', editing);
        if (error) throw error;
        setEditing(null);
      } else {
        const folderId = `fld_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
        const { error } = await supabase.from('folders').insert({
          folder_id: folderId,
          workspace_id: workspace.workspace_id,
          name: form.name.trim(),
          color: form.color,
          link_count: 0,
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
        setAdding(false);
      }
      setForm({ name: '', color: COLORS[0] });
      fetchFolders();
      toast.success(editing ? 'Folder updated' : 'Folder created');
    } catch (err) {
      toast.error(err.message || 'Failed to save folder');
    }
  };

  const handleDelete = async (folderId) => {
    try {
      const { error } = await supabase.from('folders').delete().eq('folder_id', folderId);
      if (error) throw error;
      setFolders((f) => f.filter((x) => x.folder_id !== folderId));
      toast.success('Folder deleted');
    } catch {
      toast.error('Failed to delete folder');
    }
  };

  const startEdit = (folder) => {
    setEditing(folder.folder_id);
    setForm({ name: folder.name, color: folder.color });
    setAdding(true);
  };

  const totalLinks = folders.reduce((acc, f) => acc + (f.link_count || 0), 0);

  return (
    <div className="ds-page" data-testid="folders-page">
      <div className="page-header">
        <div>
          <div className="page-label">Library · {folders.length} folders</div>
          <h1 className="page-title">Folders.</h1>
          <p className="page-sub">Organize links by campaign, channel, or team.</p>
        </div>
        <button
          type="button"
          className="btn-coral"
          onClick={() => { setAdding(true); setEditing(null); setForm({ name: '', color: COLORS[0] }); }}
        >
          <Icon.Plus /> New folder
        </button>
      </div>

      {adding && (
        <div className="settings-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 14 }}>{editing ? 'Edit folder' : 'New folder'}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input
              type="text"
              style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 9, fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', outline: 'none' }}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="e.g. Q2 Campaigns"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: form.color === c ? '3px solid var(--ink)' : '2px solid var(--line)', backgroundColor: c, cursor: 'pointer', transition: 'all 0.15s', padding: 0, transform: form.color === c ? 'scale(1.1)' : 'scale(1)' }}
                  aria-label={`Select color ${c}`}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-coral" onClick={handleSave} disabled={!form.name.trim()}>{editing ? 'Update' : 'Create'}</button>
              <button type="button" className="btn-outline" onClick={() => { setAdding(false); setEditing(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="panel" style={{ padding: 20, height: 120 }}>
              <div className="ds-skeleton" style={{ height: 36, width: 36, borderRadius: 8, marginBottom: 12 }} />
              <div className="ds-skeleton" style={{ height: 14, width: 120, marginBottom: 6 }} />
              <div className="ds-skeleton" style={{ height: 10, width: 80 }} />
            </div>
          ))}
        </div>
      ) : folders.length === 0 ? (
        <div className="panel">
          <div className="ds-empty">
            <div className="ds-empty-icon">
              <Icon.FolderBig style={{ width: 22, height: 22 }} />
            </div>
            <div className="ds-empty-title">No folders yet</div>
            <p className="ds-empty-sub">Create folders to keep your links organised by campaign or channel.</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {folders.map((folder) => (
              <button
                key={folder.folder_id}
                type="button"
                className="panel"
                onClick={() => startEdit(folder)}
                style={{ padding: 20, textAlign: 'left', cursor: 'pointer', transition: 'transform 0.18s, box-shadow 0.18s', fontFamily: 'inherit', color: 'inherit', display: 'block', width: '100%', position: 'relative', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14 }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${folder.color}20`, color: folder.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon.Folder />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{folder.name}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-dim)', fontFamily: "'Geist Mono', ui-monospace, monospace", display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon.Link /> {folder.link_count || 0} links
                </div>
                <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 4 }}>
                  <span role="button" tabIndex={-1} className="row-more" onClick={(e) => { e.stopPropagation(); startEdit(folder); }}>
                    <Icon.Pencil />
                  </span>
                  <span role="button" tabIndex={-1} className="row-more" style={{ color: 'var(--coral)' }} onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete folder "${folder.name}"?`)) handleDelete(folder.folder_id); }}>
                    <Icon.Trash />
                  </span>
                </div>
              </button>
            ))}
          </div>
          {totalLinks > 0 && (
            <div style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-faint)', fontFamily: "'Geist Mono', ui-monospace, monospace", textAlign: 'center' }}>
              {folders.length} folders · {totalLinks.toLocaleString()} links total
            </div>
          )}
        </>
      )}
    </div>
  );
}
